import { getSingleValueForTasField, setSingleValueForTasField } from './fields.ts';
import { buildSchoolFilter } from './build-school-filter.ts';
import { query } from '@warp-drive/utilities/json-api';
import type { Store } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TASCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasUnifiedInstitution as TasUnifiedInstitutionModel } from 'tio-data/data/resources/tas-unified-institution.type';

/**
 * The two `LOCK_EMPLOYEE_STATUS_BASED_ON` values this utility maps to a course date
 * field. The template field can hold other lock statuses (e.g. `FULFILLED_DATE`,
 * `ON_SUBMISSION`, `HIRE_DATE`) — those simply aren't course start/end dates and so
 * have no entry here. Kept explicit (rather than a `=== 'COURSE_END_DATE' ? … : …`
 * ternary) so any unmapped or unresolved lock status is handled deliberately instead of
 * silently defaulting to the begin field.
 */
const LOCK_STATUS_TO_DATE_FIELD: Partial<
  Record<string, 'COURSES_END_DATE' | 'COURSES_BEGIN_DATE'>
> = {
  COURSE_END_DATE: 'COURSES_END_DATE',
  COURSE_START_DATE: 'COURSES_BEGIN_DATE',
};

/**
 * Saves the courses begin/end date onto the application based on the program template's
 * lock status. Returns true if the date was newly applied; false if the lock status is
 * unresolved/unrecognized or the locked field already had a value.
 */
export function saveCoursesDate(
  date: string,
  application: TasApplicationModel,
  lockStatus: string
): boolean {
  const field = LOCK_STATUS_TO_DATE_FIELD[lockStatus];
  if (!field) {
    // Unresolved or unexpected lock status — e.g. the template's `fields` weren't
    // present when this ran. Don't guess: defaulting to COURSES_BEGIN_DATE would land
    // the date in the hidden input for an end-locked program and leave the visible,
    // required end-date input blank. Skip applying it rather than misroute it.
    return false;
  }

  const fieldValue = getSingleValueForTasField(field, application.fields);
  if (fieldValue) {
    return false;
  }
  // Reassign `fields` rather than mutating it in place. WarpDrive's cache only
  // records an attribute change (for dirty tracking + `serializePatch`) when the
  // attribute is set through `setAttr` — i.e. reassigned. An in-place mutation of
  // `application.fields` is invisible to the cache, so the date would never be
  // serialized on save and never round-trip back into the form.
  const updatedFields = { ...application.fields };
  setSingleValueForTasField(field, date, updatedFields);
  application.fields = updatedFields;
  return true;
}

/**
 * Looks up the previously selected school for a TAS program instance, if any is stored.
 * Returns undefined when no institution IDs are present or when the lookup fails.
 */
export async function loadSelectedSchool(
  programInstance: TasProgramInstanceModel,
  store: Store
): Promise<TasUnifiedInstitutionModel | undefined> {
  try {
    const tasInstitutionId = programInstance.fields['TAS_INSTITUTION_ID']?.values?.[0];
    const dapipInstitutionId = programInstance.fields['DAPIP_INSTITUTION_ID']?.values?.[0];

    if (!tasInstitutionId && !dapipInstitutionId) {
      return undefined;
    }

    const queryOptions: Record<string, string | number> = {
      'page[limit]': 1,
      'page[offset]': 0,
    };
    for (const [key, value] of Object.entries(buildSchoolFilter(programInstance))) {
      if (value !== undefined) {
        queryOptions[`filter[${key}]`] = value;
      }
    }
    const { content } = (await store.request(
      query<TasUnifiedInstitutionModel>('tas-unified-institution', queryOptions)
    )) as { content: { data: TasUnifiedInstitutionModel[] } };
    return content.data[0];
  } catch (e) {
    console.error('Error attempting to load previously selected school', e);
    return undefined;
  }
}

/**
 * Rolls back any unsaved tas-course records in the store. Call from a route's
 * `deactivate()` hook so the cleanup runs when the user leaves the route.
 */
export function rollbackUnsavedCourses(store: Store): void {
  const courses = store.peekAll('tas-course') as TASCourseModel[];
  courses.filter((course) => course.isNew).forEach((course) => course.rollbackAttributes());
}
